import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { MarketToolsBar } from "@/components/MarketToolsBar";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";

const FusionLab = lazy(() => import("@/components/games/FusionLab").then((m) => ({ default: m.FusionLab })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Palworld Pal Fusion Generator — Free Fusion Lab";
const DESCRIPTION =
  "Fuse any two Palworld Pals free: the Fusion Lab blends their names, elements, stats, work suitability and partner skill, then gives you a shareable fusion link.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Pick a base Pal", text: "Choose the Pal that keeps most of its identity — it decides the fused name's first half and the primary element." },
  { name: "Pick a donor Pal", text: "Choose the second Pal; it supplies the name's tail, the secondary element and part of every stat." },
  { name: "Read the fused stat block", text: "The lab averages HP, attack, defense, speed, ride speed, capture rate and work speed with a 2:1 weighting toward the base Pal." },
  { name: "Share the fusion link", text: "Press share to copy your hybrid and a link that rebuilds the exact same fusion for anyone who opens it." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld Pal fusion generator?", a: "The Pal Stars Fusion Lab is a free Palworld fusion generator: pick any two Pals from the 242-entry database and it returns one hybrid with a blended name, an element pair, a merged stat block, combined work suitability and one of the two partner skills." },
  { q: "Are Palworld Pal fusions real in the game?", a: "No. Fusion is a fan mechanic built by Pal Stars for fun and sharing. Palworld itself uses breeding, so use the Palworld breeding calculator when you want combinations that actually exist in-game." },
  { q: "How are the fused stats calculated?", a: "Every numeric stat is a weighted average of the two parents — two parts base Pal to one part donor — while defense and speed lean toward the donor, so the pairing order changes the result." },
  { q: "Can I share a specific Pal fusion?", a: "Yes. The share button copies a link carrying both Pal slugs, so opening it rebuilds the identical fusion with the same name and stats." },
  { q: "Is there a limit on how many fusions I can make?", a: "There is a fair-use cap of 200 fusions per browser per UTC day. It exists purely to stop automated spam; the lab is free and no fusion costs packs, spins or gold." },
];

export const Route = createFileRoute("/fusion-lab")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld Pal Fusion Lab — fuse any two Pals" },
      { property: "og:description", content: "Free Palworld fusion generator: blended names, elements, stats and work suitability with a shareable link per fusion." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/fusion-lab" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/fusion-lab" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Pal Fusion Lab", "/fusion-lab"]]),
      toolSchema({ name: "Palworld Pal Fusion Lab", description: DESCRIPTION, path: "/fusion-lab" }),
      howToSchema({ name: "How to fuse two Palworld Pals", description: "Pick two Pals, read the fused stat block and share the fusion link.", path: "/fusion-lab", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/fusion-lab"),
    ],
  }),
  component: FusionLabPage,
});

function FusionLabPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · fan mechanic</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld Pal Fusion Lab</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          The Palworld Pal fusion generator blends any two Pals into one hybrid: a merged name, an element pair, a
          weighted stat block, combined work suitability and one inherited partner skill. Fusion is a fan mechanic, not
          an in-game feature, and every fusion has its own share link.
        </p>
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Fuse two Palworld Pals</h2><p className="mt-2 text-sm text-muted-foreground">Loading the fusion lab…</p></section>}>
        <FusionLab />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How fusion works</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="fusion-lab" heading="Fusion Lab badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/breeding" className="font-semibold text-primary hover:underline">Palworld breeding calculator →</Link> real in-game combinations.</li>
          <li><Link to="/whos-that-pal" className="font-semibold text-primary hover:underline">Who's That Pal? quiz →</Link></li>
          <li><Link to="/pals" className="font-semibold text-primary hover:underline">Palworld Pal database →</Link></li>
        </ul>
      </section>

      <MarketToolsBar />

      <FaqSection items={FAQ} heading="Palworld fusion generator FAQ" />
    </div>
  );
}
